import * as services from "../services/services";

const text = (value) => (value == null ? "" : String(value).trim());

const getBowOwner = (bowPet) => bowPet?.owners?.[0] || bowPet?.owner || null;

const getOwnerValue = (owner, keys) => {
  for (const key of keys) {
    const value = text(owner?.[key]);
    if (value) return value;
  }

  return "";
};

const normalizeBowOwner = (bowOwner) => {
  if (!bowOwner) return null;

  return {
    bowId: getOwnerValue(bowOwner, ["id", "bowId", "bow_id"]),
    firstName: getOwnerValue(bowOwner, ["first_name", "firstName"]),
    lastName: getOwnerValue(bowOwner, ["last_name", "lastName"]),
    phoneNumber: getOwnerValue(bowOwner, ["phone_number", "phoneNumber"]),
    idCard: getOwnerValue(bowOwner, [
      "id_card",
      "idCard",
      "id_card_number",
      "citizen_id",
      "citizenId",
    ]),
    email: getOwnerValue(bowOwner, ["email"]),
    imageUrl: getOwnerValue(bowOwner, ["image_url", "imageUrl", "image"]),
    lineUserId: getOwnerValue(bowOwner, ["line_user_id", "lineUserId"]),
  };
};

const sameText = (a, b) => text(a).toLowerCase() === text(b).toLowerCase();

const responseList = (res) => res?.data?.data || res?.data?.results || res?.data || [];

const findMatchingOwner = (owners, owner) => {
  if (!owner) return null;

  return owners.find((item) => {
    if (owner.bowId && sameText(item.bowId || item.bow_id, owner.bowId)) {
      return true;
    }
    if (owner.idCard && sameText(item.idCard, owner.idCard)) return true;
    if (owner.phoneNumber && sameText(item.phoneNumber, owner.phoneNumber)) {
      return true;
    }
    if (owner.email && sameText(item.email, owner.email)) return true;
    if (owner.lineUserId && sameText(item.lineUserId, owner.lineUserId)) {
      return true;
    }

    return (
      owner.firstName &&
      owner.lastName &&
      sameText(item.firstName, owner.firstName) &&
      sameText(item.lastName, owner.lastName)
    );
  });
};

const findOrCreateOwner = async (ownerPayload) => {
  if (
    !ownerPayload?.bowId &&
    !ownerPayload?.firstName &&
    !ownerPayload?.phoneNumber &&
    !ownerPayload?.idCard
  ) {
    return null;
  }

  const ownersRes = await services.getOwnersByHospital();
  const owners = responseList(ownersRes);
  const existingOwner = findMatchingOwner(owners, ownerPayload);

  if (existingOwner) return existingOwner;

  try {
    const createdRes = await services.createOwner(ownerPayload);
    return createdRes?.data?.data || createdRes?.data || null;
  } catch (err) {
    if ((err?.response?.status || err?.status) !== 409) throw err;

    const retryRes = await services.getOwnersByHospital();
    const retryOwners = responseList(retryRes);
    return findMatchingOwner(retryOwners, ownerPayload);
  }
};

const getTreatmentData = (treatmentResult) => treatmentResult?.data?.data || {};

const getLocalPetIdFromTreatment = (treatmentResult) => {
  const data = getTreatmentData(treatmentResult);

  return (
    data.petId ||
    data.pet?.id ||
    data.pet?.petId ||
    data.treatment?.petId ||
    data.treatments?.[0]?.petId ||
    null
  );
};

const findLocalPetIdByBowPet = async (bowPet) => {
  const keywords = [
    text(bowPet?.id),
    text(bowPet?.microchip_number),
  ].filter(Boolean);

  for (const keyword of keywords) {
    const res = await services.getPetsBykeyword(keyword);
    const pets = responseList(res);
    const matchedPet = pets.find(
      (item) =>
        sameText(item.petCode, bowPet?.id) ||
        sameText(item.microchipNumber, bowPet?.microchip_number),
    );

    if (matchedPet?.id) return matchedPet.id;
  }

  return null;
};

export const buildLocalOwnerFromBowPet = (bowPet) => {
  const owner = normalizeBowOwner(getBowOwner(bowPet));
  if (!owner) return null;

  return {
    bowId: owner.bowId,
    firstName: owner.firstName,
    lastName: owner.lastName,
    phoneNumber: owner.phoneNumber,
    idCard: owner.idCard,
    email: owner.email,
    image: owner.imageUrl,
    imageUrl: owner.imageUrl,
    lineUserId: owner.lineUserId,
  };
};

export const syncBowOwnerToLocalPet = async ({ bowPet, treatmentResult }) => {
  const ownerPayload = buildLocalOwnerFromBowPet(bowPet);
  if (!ownerPayload) return null;

  const owner = await findOrCreateOwner(ownerPayload);
  if (!owner?.id) return null;

  const localPetId =
    getLocalPetIdFromTreatment(treatmentResult) ||
    (await findLocalPetIdByBowPet(bowPet));

  if (!localPetId) return owner;

  await services.updatePetOwner(localPetId, {
    ownerId: owner.id,
  });

  return owner;
};
